/**
 * InstanceMailPanel (OCP-591) - tenant-included instance mail on the
 * tenant detail: wiring state (local stamps + the live OMS view: sender
 * domain verification, fair-use budget, this-month usage) and the
 * "Wire instance mail" action (provision + env inject + redeploy - also
 * the backfill button for tenants that predate the mail step). Key
 * material never reaches this surface.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

interface InstanceMailState {
  configured: boolean;
  wired_at: string | null;
  from: string | null;
  remote: {
    sender_domain?: string;
    sender_domain_status?: string | null;
    included_monthly?: number;
    used_this_month?: number;
    overage_rate_microcents?: number;
    active?: boolean;
  } | null;
}

export function InstanceMailPanel(props: { tenantId: string; provisioned: boolean }) {
  const [state, setState] = useState<InstanceMailState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [wiring, setWiring] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await api<InstanceMailState>(`/v1/admin/tenants/${props.tenantId}/instance-mail`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, [props.tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function wire() {
    setWiring(true);
    setNotice(null);
    try {
      const res = await api<{ from: string; key_minted: boolean }>(
        `/v1/admin/tenants/${props.tenantId}/wire-instance-mail`,
        { method: 'POST' },
      );
      setNotice(
        res.key_minted
          ? `Wired - key minted, sending as ${res.from}. Tenant is redeploying.`
          : `Re-wired - existing key kept, budget refreshed (${res.from}). Tenant is redeploying.`,
      );
      void load();
    } catch (err) {
      setNotice(null);
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setWiring(false);
    }
  }

  if (!state) return null;

  const remote = state.remote;
  return (
    <section className="mt-10">
      <h2 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100">Instance mail</h2>
      <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 text-sm">
        {!state.configured && (
          <p className="text-gray-500 dark:text-gray-400">
            OMS bridge not configured on this deploy - instance mail cannot be wired.
          </p>
        )}
        {state.configured && (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <div className="text-gray-400">Wired</div>
                <div className="text-gray-700 dark:text-gray-300">
                  {state.wired_at ? fmtDateTime(state.wired_at) : 'not wired'}
                </div>
              </div>
              <div>
                <div className="text-gray-400">Sender</div>
                <div className="text-gray-700 dark:text-gray-300">
                  {state.from ?? '-'}
                  {remote?.sender_domain_status && (
                    <span
                      className={
                        remote.sender_domain_status === 'verified'
                          ? 'ml-1 text-xs text-green-700 dark:text-green-400'
                          : 'ml-1 text-xs text-amber-700 dark:text-amber-400'
                      }
                    >
                      ({remote.sender_domain_status})
                    </span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-gray-400">Fair-use this month</div>
                <div className="text-gray-700 dark:text-gray-300">
                  {remote
                    ? `${remote.used_this_month ?? 0} / ${remote.included_monthly ?? '-'}`
                    : state.wired_at
                      ? 'no OMS budget - re-wire'
                      : '-'}
                </div>
              </div>
            </div>
            {props.provisioned && (
              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => void wire()}
                  disabled={wiring}
                  className="rounded bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-700 disabled:opacity-50 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-300"
                >
                  {wiring ? 'Wiring…' : state.wired_at ? 'Re-wire / refresh budget' : 'Wire instance mail'}
                </button>
                {notice && <span className="text-xs text-green-700 dark:text-green-400">{notice}</span>}
              </div>
            )}
          </>
        )}
        {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </section>
  );
}
